import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ImportCounts, ImportResponse } from '@cleartask/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { useCurrentUser } from '../../session.js';
import { useToast } from '../../components/ui/Toast.js';

const COLECOES: Array<{ chave: keyof ImportResponse['summary']; nome: string }> = [
  { chave: 'tasks', nome: 'Tarefas' },
  { chave: 'lists', nome: 'Listas' },
  { chave: 'tags', nome: 'Tags' },
  { chave: 'habits', nome: 'Hábitos' },
  { chave: 'habitLogs', nome: 'Dias de hábito' },
  { chave: 'countdowns', nome: 'Contagens' },
];

function entram(summary: ImportResponse['summary']): number {
  return Object.values(summary).reduce((soma, c) => soma + c.added + c.updated, 0);
}

function descrever(c: ImportCounts): string {
  const partes: string[] = [];
  if (c.added > 0) partes.push(`${c.added} ${c.added === 1 ? 'nova' : 'novas'}`);
  if (c.updated > 0) partes.push(`${c.updated} ${c.updated === 1 ? 'atualizada' : 'atualizadas'}`);
  if (c.kept > 0) partes.push(`${c.kept} já ${c.kept === 1 ? 'estava' : 'estavam'} aqui`);
  if (c.skipped > 0) partes.push(`${c.skipped} de fora`);
  return partes.join(' · ');
}

/**
 * Importar um backup (PROXIMOS_PASSOS 3.4).
 *
 * Sempre em dois passos: a previa (`dryRun`) mostra o que entraria, e so o
 * segundo botao grava. Um import direto, num arquivo escolhido por engano, so
 * seria percebido depois — e o import nao tem desfazer.
 *
 * O texto do arquivo fica em memoria entre os dois passos, e o servidor
 * recalcula o plano na gravacao: o que vale e o banco do momento da
 * confirmacao, nao o da previa.
 */
export function ImportarBackup() {
  const user = useCurrentUser();
  const qc = useQueryClient();
  const { showToast } = useToast();
  const entrada = useRef<HTMLInputElement>(null);

  const [arquivo, setArquivo] = useState<{ texto: string; previa: ImportResponse } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  function limpar() {
    setArquivo(null);
    // Sem zerar o input, escolher o MESMO arquivo de novo nao dispara `change`.
    if (entrada.current) entrada.current.value = '';
  }

  async function escolher(file: File | undefined) {
    if (!file) return;
    setErro(null);
    setOcupado(true);
    try {
      const texto = await file.text();
      const previa = await api.importData(texto, true);
      setArquivo({ texto, previa });
    } catch (err) {
      limpar();
      setErro(
        err instanceof ApiClientError ? err.message : 'Não foi possível ler o arquivo agora.',
      );
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar() {
    if (!arquivo) return;
    setOcupado(true);
    try {
      const { summary } = await api.importData(arquivo.texto, false);
      limpar();
      // Tudo pode ter mudado: tarefas, listas, tags, habitos, contagens.
      await qc.invalidateQueries();
      showToast({ message: `Importado: ${entram(summary)} itens.` });
    } catch (err) {
      setErro(err instanceof ApiClientError ? err.message : 'Não foi possível importar agora.');
    } finally {
      setOcupado(false);
    }
  }

  const previa = arquivo?.previa;
  const total = previa ? entram(previa.summary) : 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <label
          className={`flex min-h-touch cursor-pointer items-center rounded-sm bg-surface-2 px-4 font-medium ${
            ocupado ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          Importar JSON
          <input
            ref={entrada}
            type="file"
            accept="application/json,.json"
            className="sr-only"
            disabled={ocupado}
            onChange={(e) => void escolher(e.target.files?.[0])}
          />
        </label>
      </div>

      {erro && (
        <p role="alert" className="text-sm text-overdue">
          {erro}
        </p>
      )}

      {previa && (
        <div
          role="region"
          aria-label="Prévia do import"
          className="flex flex-col gap-2 rounded-sm bg-surface-2 p-3 text-sm"
        >
          <p>
            Arquivo de <strong className="font-medium">{previa.source.email}</strong>, gerado em{' '}
            {new Date(previa.source.generatedAt).toLocaleString('pt-BR', {
              timeZone: user.timezone,
              dateStyle: 'short',
              timeStyle: 'short',
            })}
            .
          </p>

          {previa.fromAnotherUser && (
            <p className="font-medium">
              Este arquivo é de outra conta. O que entrar fica em seu nome.
            </p>
          )}

          <ul className="flex flex-col gap-1">
            {COLECOES.map(({ chave, nome }) => {
              const texto = descrever(previa.summary[chave]);
              return texto ? (
                <li key={chave}>
                  <span className="font-medium">{nome}:</span> {texto}
                </li>
              ) : null;
            })}
          </ul>

          {total === 0 ? (
            <p>Nada a importar: tudo o que o arquivo tem já está aqui.</p>
          ) : (
            <p className="text-text-mut">
              Nada do que já está aqui é apagado ou volta a uma versão anterior.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {total > 0 && (
              <button
                type="button"
                disabled={ocupado}
                onClick={() => void confirmar()}
                className="min-h-touch rounded-sm bg-brand-blue px-4 font-medium text-white disabled:opacity-60"
              >
                Importar {total} {total === 1 ? 'item' : 'itens'}
              </button>
            )}
            <button
              type="button"
              disabled={ocupado}
              onClick={limpar}
              className="min-h-touch rounded-sm bg-surface px-4 font-medium"
            >
              {total === 0 ? 'Fechar' : 'Cancelar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
